/**
 * buildDmlCountProbeSql — pure, per-engine `SELECT COUNT(*)` probe builder for
 * the DML blast-radius chip (Advisor 2 §A4, finding DML-06 + the estimate half
 * of DML-01/03).
 *
 * Given an UPDATE/DELETE statement outline + the raw buffer + parse options, it
 * slices the target, any FROM/USING/join tail and the WHERE span **verbatim
 * from the buffer using the outline offsets** (never re-tokenising) and emits a
 * `SELECT COUNT(*) FROM <target [tail]> WHERE <where verbatim>` the engine will
 * accept.
 *
 * Honesty scope — the whole point is to NEVER fabricate a number:
 *  - Simple single-table forms → an EXACT probe (`approximate: false`). A form
 *    with no WHERE counts the whole table (powers the chip's "all rows (≈ N)").
 *  - Join forms (PG `UPDATE…FROM` / `DELETE…USING` comma-join, MySQL/MSSQL
 *    multi-table + join tails) → the count is matching row *combinations*,
 *    which can over-count touched rows, so `approximate: true` and the chip
 *    labels it `~N matching rows` with an explaining tooltip.
 *  - Shapes that can't be transplanted safely → `null` (the chip renders the
 *    honest "n/a for this shape" state): a correlated-subquery SET (incl. the
 *    Oracle correlated update), a join/USING/FROM tail with no WHERE to bound
 *    it, or a DELETE with no resolvable target.
 */

import type { ParseOptions, SqlRange } from '../../../sql-grammar/model';
import { skipContextFor, trySkipNonCode } from '../../../sql-resolver/classify/tokenSkip';
import type { SkipContext } from '../../../sql-resolver/classify/tokenSkip';
import { extractDeleteOutline, type DeleteOutline } from '../render/delete/extractDeleteOutline';
import {
  IDENT_CONT,
  IDENT_START,
  isWordBoundaryBefore,
} from '../render/insert/insertParseHelpers';
import { extractUpdateOutline, type UpdateOutline } from '../render/update/extractUpdateOutline';
import type { RenderMode } from '../types';
import { readBareAliasTail } from '../render/update/resolveDmlTargetAlias';

/** The COUNT(*) probe for a DML statement, plus whether its count is a join-form approximation. */
export interface DmlCountProbe {
  /** `SELECT COUNT(*) FROM …` — accepted by the target engine as written. */
  readonly sql: string;
  /**
   * True for join/multi-relation forms: the COUNT is a matching-row-combination
   * count that can be higher than the number of rows actually changed.
   */
  readonly approximate: boolean;
}

export type DmlProbeInput =
  | {
      readonly verb: 'update';
      readonly outline: UpdateOutline;
      readonly bufferText: string;
      readonly options: ParseOptions;
    }
  | {
      readonly verb: 'delete';
      readonly outline: DeleteOutline;
      readonly bufferText: string;
      readonly options: ParseOptions;
    };

export function buildDmlCountProbeSql(input: DmlProbeInput): DmlCountProbe | null {
  return input.verb === 'update'
    ? buildUpdateProbe(input.outline, input.bufferText, input.options)
    : buildDeleteProbe(input.outline, input.bufferText);
}

export interface DmlEstimate {
  readonly verb: 'update' | 'delete';
  readonly hasWhere: boolean;
  readonly probe: DmlCountProbe | null;
}

/** The blast-radius estimate for the UPDATE / DELETE at `range`; null for any other statement kind. */
export function buildDmlEstimate(
  mode: RenderMode | undefined,
  bufferText: string,
  range: SqlRange,
  options: ParseOptions,
): DmlEstimate | null {
  if (mode === 'update') {
    const outline = extractUpdateOutline(bufferText, range, options);
    return { verb: mode, hasWhere: outline.whereStart !== null, probe: buildUpdateProbe(outline, bufferText, options) };
  }
  if (mode !== 'delete') return null;
  const outline = extractDeleteOutline(bufferText, range, options);
  return { verb: mode, hasWhere: outline.whereStart !== null, probe: buildDeleteProbe(outline, bufferText) };
}

// ── UPDATE ──────────────────────────────────────────────────────────────────

function buildUpdateProbe(
  outline: UpdateOutline,
  buffer: string,
  options: ParseOptions,
): DmlCountProbe | null {
  if (!outline.hasUpdate || outline.targetText === null) return null;
  const ctx = skipContextFor(options);

  // Correlated-subquery SET (`SET x = (SELECT … WHERE … = t.id)`) — incl. the
  // Oracle correlated update — can't be transplanted into a FROM. Any subquery
  // in SET is treated conservatively as unbounded → null.
  if (
    outline.setStart !== null &&
    outline.setEnd !== null &&
    spanContainsKeyword(buffer, outline.setStart, outline.setEnd, ctx, 'SELECT')
  ) {
    return null;
  }

  const target = outline.targetText.trim();
  const whereBody = sliceWhere(buffer, outline.whereStart, outline.whereEnd);
  const hasWhere = whereBody !== '';

  const hasFrom = outline.fromStart !== null && outline.fromEnd !== null;
  const hasMulti = outline.multiTableStart !== null && outline.multiTableEnd !== null;

  // Distinguish a real join tail from a bare trailing alias (`UPDATE film f SET …`).
  const aliasTail = hasMulti
    ? readBareAliasTail(buffer, outline.multiTableStart as number, outline.multiTableEnd as number, options)
    : null;
  const multiText = hasMulti
    ? sliceTrim(buffer, outline.multiTableStart as number, outline.multiTableEnd as number)
    : '';
  const isPureAlias = aliasTail !== null && !aliasTail.hasJoins;

  // PG / MSSQL `UPDATE … SET … FROM …`.
  if (hasFrom) {
    if (!hasWhere) return null; // join tail with nothing to bound it
    const fromBody = sliceTrim(buffer, outline.fromStart as number, outline.fromEnd as number);
    const targetRef = isPureAlias ? `${target} ${multiText}` : target;
    const fromClause = tailReferencesTarget(fromBody, targetRef)
      ? fromBody
      : `${targetRef}, ${fromBody}`;
    return { sql: `SELECT COUNT(*) FROM ${fromClause} WHERE ${whereBody}`, approximate: true };
  }

  // MySQL `UPDATE t1 JOIN t2 …` / `UPDATE t1, t2 …` (join before SET).
  if (hasMulti && !isPureAlias) {
    if (!hasWhere) return null; // join tail with nothing to bound it
    return {
      sql: `SELECT COUNT(*) FROM ${target} ${multiText} WHERE ${whereBody}`,
      approximate: true,
    };
  }

  // Simple single-table (optionally with a trailing alias) — exact count unless
  // a trailing MySQL `LIMIT n` caps the touched rows (I7): the COUNT then
  // over-reports (LIMIT applies AFTER aggregation), so it's an upper bound.
  const targetRef = isPureAlias ? `${target} ${multiText}` : target;
  return simpleProbe(targetRef, whereBody, hasWhere, outline.limitStart !== null);
}

// ── DELETE ──────────────────────────────────────────────────────────────────

function buildDeleteProbe(outline: DeleteOutline, buffer: string): DmlCountProbe | null {
  if (!outline.hasDelete || outline.targetText === null) return null;

  const target = outline.targetText.trim();
  const whereBody = sliceWhere(buffer, outline.whereStart, outline.whereEnd);
  const hasWhere = whereBody !== '';

  const hasMulti = outline.multiTableStart !== null && outline.multiTableEnd !== null;
  const hasUsing = outline.usingStart !== null && outline.usingEnd !== null;

  // The FROM base is the target plus any join tail that sits directly after it
  // (MySQL `DELETE FROM t1 JOIN t2 …` / `DELETE t1,t2 FROM t1 JOIN t2 …`).
  let base = target;
  let joinForm = false;
  if (hasMulti) {
    const multiText = sliceTrim(buffer, outline.multiTableStart as number, outline.multiTableEnd as number);
    base = `${target} ${multiText}`;
    joinForm = true;
  }

  // PG / MySQL `DELETE FROM t USING …`.
  if (hasUsing) {
    if (!hasWhere) return null; // join tail with nothing to bound it
    const usingBody = sliceTrim(buffer, outline.usingStart as number, outline.usingEnd as number);
    const fromClause = tailReferencesTarget(usingBody, base) ? usingBody : `${base}, ${usingBody}`;
    return { sql: `SELECT COUNT(*) FROM ${fromClause} WHERE ${whereBody}`, approximate: true };
  }

  if (joinForm) {
    if (!hasWhere) return null; // join tail with nothing to bound it
    return { sql: `SELECT COUNT(*) FROM ${base} WHERE ${whereBody}`, approximate: true };
  }

  // Simple single-table DELETE (target may carry an alias) — exact count unless
  // a trailing MySQL `LIMIT n` caps the touched rows (I7): upper bound only.
  return simpleProbe(base, whereBody, hasWhere, outline.limitStart !== null);
}

// ── Shared helpers ────────────────────────────────────────────────────────────

function simpleProbe(
  fromClause: string,
  whereBody: string,
  hasWhere: boolean,
  limitCaps = false,
): DmlCountProbe {
  const sql = hasWhere
    ? `SELECT COUNT(*) FROM ${fromClause} WHERE ${whereBody}`
    : `SELECT COUNT(*) FROM ${fromClause}`;
  // A trailing `LIMIT n` means the real touched rows is ≤ this COUNT → label it
  // approximate (upper bound), never an exact "N rows affected".
  return { sql, approximate: limitCaps };
}

function sliceTrim(source: string, start: number, end: number): string {
  return source.slice(start, end).trim();
}

/** WHERE body verbatim, minus a trailing statement terminator the range may include. */
function sliceWhere(source: string, start: number | null, end: number | null): string {
  if (start === null || end === null) return '';
  return source.slice(start, end).trim().replace(/;+\s*$/, '').trimEnd();
}

/**
 * SQL keywords that appear in a relation tail but are not relation names —
 * excluded from the reference-token comparison so `AS`/`ON`/`JOIN` never
 * spuriously match a target token.
 */
const TAIL_STOPWORDS: ReadonlySet<string> = new Set([
  'as', 'on', 'and', 'or', 'not', 'in', 'is', 'inner', 'left', 'right', 'full',
  'cross', 'outer', 'join', 'using', 'where', 'from', 'natural', 'lateral',
]);

function collectRefTokens(text: string): Set<string> {
  const out = new Set<string>();
  for (const match of text.matchAll(/[A-Za-z_][A-Za-z0-9_]*/g)) {
    const token = match[0].toLowerCase();
    if (!TAIL_STOPWORDS.has(token)) out.add(token);
  }
  return out;
}

/**
 * Heuristic: does `tailBody` already reference the target relation/alias? When
 * it does the target is a relation inside the tail (MSSQL `UPDATE p … FROM …
 * Product p …`) and must NOT be comma-prepended; when it doesn't the target is
 * a separate relation (PG `UPDATE t … FROM src`) and IS prepended. Wrong either
 * way degrades to an honest state (a failed probe → `≈ ?`, or a labelled
 * over-count), never a silent lie.
 */
function tailReferencesTarget(tailBody: string, targetRef: string): boolean {
  const targetTokens = collectRefTokens(targetRef);
  const tailTokens = collectRefTokens(tailBody);
  for (const token of targetTokens) if (tailTokens.has(token)) return true;
  return false;
}

/** Token-aware scan for a bare `keyword` at any paren depth, skipping strings / comments / quoted ids. */
function spanContainsKeyword(
  source: string,
  start: number,
  end: number,
  ctx: SkipContext,
  keyword: string,
): boolean {
  const upper = keyword.toUpperCase();
  let i = start;
  while (i < end) {
    const skipped = trySkipNonCode(source, i, end, ctx);
    if (skipped >= 0) {
      i = skipped;
      continue;
    }
    const c = source[i];
    if (IDENT_START.test(c) && isWordBoundaryBefore(source, i)) {
      let j = i + 1;
      while (j < end && IDENT_CONT.test(source[j])) j++;
      if (source.slice(i, j).toUpperCase() === upper) return true;
      i = j;
      continue;
    }
    i++;
  }
  return false;
}
